import {BLOCKS_FORMAT, BlocksProject, BlocksSprite} from './projectTypes.ts';

/*
 * Builds the translate-panel payload for a Blocks project: the author-written
 * content (say-text, names, messages) as a readable glossary — NOT the editor
 * chrome or block labels, which are UI localization, not project content.
 */

// Deep-walk a serialized Blockly workspace collecting the string literals
// typed into text inputs (say blocks, join, etc.).
function collectTextLiterals(node: unknown, out: string[]): void {
    if (!node || typeof node !== 'object') {
        return;
    }

    if (Array.isArray(node)) {
        node.forEach(item => collectTextLiterals(item, out));
        return;
    }

    const record = node as Record<string, unknown>;
    const fields = record.fields as Record<string, unknown> | undefined;
    if (
        record.type === 'text' &&
        typeof fields?.TEXT === 'string' &&
        fields.TEXT.trim()
    ) {
        out.push(fields.TEXT.trim());
    }

    Object.values(record).forEach(value => collectTextLiterals(value, out));
}

// The code typed into code blocks: author content too.
function collectCode(node: unknown, out: string[]): void {
    if (!node || typeof node !== 'object') {
        return;
    }
    if (Array.isArray(node)) {
        node.forEach(item => collectCode(item, out));
        return;
    }
    const record = node as Record<string, unknown>;
    const fields = record.fields as Record<string, unknown> | undefined;
    if (
        typeof record.type === 'string' &&
        record.type.startsWith('code_') &&
        typeof fields?.CODE === 'string' &&
        fields.CODE.trim()
    ) {
        out.push(fields.CODE.trim());
    }
    Object.values(record).forEach(value => collectCode(value, out));
}

function spriteSection(sprite: BlocksSprite): string[] {
    const lines = [`Sprite: ${sprite.name}`];

    const said: string[] = [];
    collectTextLiterals(sprite.workspace, said);
    if (said.length > 0) {
        lines.push(
            ...[...new Set(said)].map(text => `  Says: "${text}"`)
        );
    }

    const code: string[] = [];
    collectCode(sprite.workspace, code);
    if (code.length > 0) {
        lines.push(...[...new Set(code)].map(text => `  Code: ${text}`));
    }

    if (sprite.costumes.length > 0) {
        lines.push(
            `  Costumes: ${sprite.costumes.map(c => c.name).join(', ')}`
        );
    }
    if (sprite.sounds.length > 0) {
        lines.push(`  Sounds: ${sprite.sounds.map(s => s.name).join(', ')}`);
    }

    return lines;
}

// Returns the glossary text, or null when the scene script is not a Blocks
// project document (callers fall back to their generic behaviour).
export default function extractBlocksProjectText(
    sceneScript: string | null | undefined
): string | null {
    if (!sceneScript) {
        return null;
    }

    let project: BlocksProject;
    try {
        const parsed = JSON.parse(sceneScript);
        if (!parsed || parsed.format !== BLOCKS_FORMAT) {
            return null;
        }
        project = parsed as BlocksProject;
    } catch {
        return null;
    }

    const sections: string[] = [];

    for (const sprite of project.sprites ?? []) {
        sections.push(spriteSection(sprite).join('\n'));
    }

    const backdrops = (project.stage?.backdrops ?? [])
        .map(backdrop => backdrop.name)
        .filter(Boolean);
    if (backdrops.length > 0) {
        sections.push(`Backdrops: ${backdrops.join(', ')}`);
    }

    const messages = (project.messages ?? []).filter(Boolean);
    if (messages.length > 0) {
        sections.push(`Broadcast messages: ${messages.join(', ')}`);
    }

    // Variables live in each sprite's serialized workspace (global by name).
    const variables = new Set<string>(
        (project.variables ?? []).map(variable => variable.name)
    );
    for (const sprite of project.sprites ?? []) {
        const workspace = sprite.workspace as {
            variables?: {name?: string}[];
        } | null;
        for (const variable of workspace?.variables ?? []) {
            if (variable.name) {
                variables.add(variable.name);
            }
        }
    }
    if (variables.size > 0) {
        sections.push(`Variables: ${[...variables].join(', ')}`);
    }

    return sections.join('\n\n');
}
